import { MaterialLibrary, MaterialUpdater, ResourceLoader, ResourceLoadTransaction } from 'webgl-helpers';
import * as THREE from 'three';
import { IJungGeometry, IJungMaterialOptions } from '../types';
import { MaterialAssignment } from '../../../assets/types/programSchema.json';
import { MaterialOptionHandler } from './MaterialOptionHandler';
import { TextureLoader } from './TextureLoader';
import { MaterialOption } from '../../../assets/types/materialOptionsSchema.json';
import {
    MISSING_MATERIAL_PREFIX,
    MissingAssetDetail,
    notifyMissingAsset,
} from '../../MissingAssets/missingAssetsBus';
export { MISSING_ASSET_EVENT, MISSING_MATERIAL_PREFIX } from '../../MissingAssets/missingAssetsBus';
export type { MissingAssetDetail } from '../../MissingAssets/missingAssetsBus';

type GeoIdentifier = {
    geoId: string
    productId: string
    assetName: string
}

export const depthMask = 'DepthMask';

// Per-session debug switch (?debug=1) — same flag the MissingAssetsBadge uses. When on, setMaterial
// dumps a per-geometry table of slot → material so the full current-model material map is visible.
const DEBUG_MATERIALS = typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('debug') === '1';

export class MaterialManager {

    private _renderer!: THREE.WebGLRenderer

    constructor(
        private materialLibrary: MaterialLibrary,
        private loader: ResourceLoader,
        private updater: MaterialUpdater<string>,
        private optionHandler: MaterialOptionHandler,
        private textureLoader: TextureLoader,
        // Per-context material name remap (e.g. AVIVA: ch -> champ_new). Lets a program
        // swap the library material for shared geometry without touching materialization.csv.
        private materialOverrides: Record<string, string> = {}
    ) {
    }

    /** Resolve a material name through the per-context override map (identity if no override). */
    private resolveAssetName(assetName: string): string {
        return this.materialOverrides[assetName] ?? assetName;
    }

    /**
     * Final library material name for an assignment: strip the importer's __missing__ sentinel,
     * then apply the per-context override. This is the name actually looked up in the library AND
     * the name reported to the AR service (via options.definition.name) so AR matches the viewer.
     */
    private resolveLibraryName(assetName: string): string {
        const base = assetName.startsWith(MISSING_MATERIAL_PREFIX)
            ? assetName.substring(MISSING_MATERIAL_PREFIX.length)
            : assetName;
        return this.resolveAssetName(base);
    }

    set renderer(value: THREE.WebGLRenderer) {
        this._renderer = value;
    }

    public async loadMaterials(libraryPath: string, onProgress = () => {
    }) {
        const transaction = new ResourceLoadTransaction([
            ResourceLoadTransaction.Model(libraryPath)
        ] as const);

        const res = await this.loader.load(transaction, onProgress);
        if (!res) {

            throw new Error('Unable to read data from ' + libraryPath);
        }

        this.materialLibrary.addMaterialsFromGroup(res[0].group);
    }

    public async setMaterial(geo: THREE.Object3D, def: IJungGeometry): Promise<IJungMaterialOptions[]> {
        console.log(`Materializing ${def.id} `)
        const materials = def.definition.assignment.data.materials;
        const createdOptions: IJungMaterialOptions[]= [];
        const debugRows: Array<Record<string, unknown>> = [];

        for (const assignment of materials) {
            let materialAsset = this.getMaterialAsset(assignment);
            if (!materialAsset) {
                const resolvedName = this.resolveAssetName(assignment.assetName);
                const originalName = resolvedName.startsWith(MISSING_MATERIAL_PREFIX)
                    ? resolvedName.substring(MISSING_MATERIAL_PREFIX.length)
                    : resolvedName;
                // console.info — survives debug.ts production-mode override (which silences log/warn/error).
                console.info(`[missing-material] ${originalName} on ${def.definition.assignment.data.assetName} (product ${def.id}) — using magenta placeholder.`);
                this.notifyMissingAsset({
                    kind: 'material',
                    assetName: originalName,
                    productId: def.id,
                    geometryName: def.definition.assignment.data.assetName,
                });
                materialAsset = this.createMissingMaterialPlaceholder(originalName);
            }
            if (DEBUG_MATERIALS) {
                const wasMissing = assignment.assetName.startsWith(MISSING_MATERIAL_PREFIX);
                const requested = wasMissing
                    ? assignment.assetName.substring(MISSING_MATERIAL_PREFIX.length)
                    : assignment.assetName;
                const applied = this.resolveAssetName(requested);
                const isMissing = !!(materialAsset.userData && (materialAsset.userData as { missing?: boolean }).missing);
                debugRows.push({
                    slot: assignment.slot,
                    requested,
                    applied: isMissing ? '—' : applied,
                    primary: !!assignment.primary,
                    status: isMissing
                        ? 'MISSING (magenta)'
                        : applied !== requested
                            ? 'override'
                            : wasMissing ? 'rescued from lib' : 'ok',
                });
            }
            const options = await this.createMaterialOptions(geo, assignment, materialAsset);
            if (options) {
                if (materialAsset.userData && materialAsset.userData.depthMask) {
                    options.depthMask = true;
                }
                createdOptions.push(options);
                this.updater.updateMaterial(geo, options);
            }
        }
        if (DEBUG_MATERIALS && debugRows.length > 0) {
            debugRows.sort((a, b) => (a.slot as number) - (b.slot as number));
            // console.info/table survive debug.ts production silencing (it only nukes log/warn/error).
            console.info(`[materials] ${def.definition.assignment.data.assetName} (product ${def.id}) — ${debugRows.length} slots`);
            // eslint-disable-next-line no-console
            console.table(debugRows);
        }
        return createdOptions;
    }

    private createMissingMaterialPlaceholder(assetName: string): THREE.Material {
        const placeholder = new THREE.MeshBasicMaterial({ color: 0xff00ff });
        placeholder.name = `__missing__${assetName}`;
        placeholder.userData = { missing: true, assetName };
        return placeholder;
    }

    private notifyMissingAsset(detail: MissingAssetDetail): void {
        notifyMissingAsset(detail);
    }

    private getMaterialAsset(material: MaterialAssignment): THREE.Material | undefined {
        if (material.assetName === depthMask) {
            const material = new THREE.MeshBasicMaterial();
            material.blending = THREE.CustomBlending; // 5
            material.blendEquation = THREE.AddEquation; //default 100
            material.blendSrc = THREE.ZeroFactor;  // 200
            material.blendDst = THREE.OneFactor; // 201
            material.userData = { depthMask: true}

            return material;
        }
        // Strip the importer's __missing__ sentinel and try to resolve against the (possibly
        // updated) library. This means a material renders as soon as it exists in the library by
        // its real name (e.g. adding `kristallgrau` to the lib) OR a per-context override remaps it
        // (graphitschwarzMatt -> anm). Falls through to the magenta placeholder only if still not found.
        const asset = this.materialLibrary.getMaterial(this.resolveLibraryName(material.assetName));
        if (!asset) {
            return;
        }
        return asset.clone()
    }

    private async createMaterialOptions(geo: THREE.Object3D, materialData: MaterialAssignment, materialAsset: THREE.Material): Promise<IJungMaterialOptions|undefined> {
        const geoData:IJungGeometry = geo.userData as IJungGeometry;
        console.log(`Find material ${materialData.assetName} for slot ${materialData.slot}`)
        const geoIdentifier: GeoIdentifier = {geoId: geo.name, productId: geoData.id, assetName: geoData.definition.assignment.data.assetName}
        const area = this.findMaterialAreaName(geo, materialData.slot);
        if (!area) {
            console.warn(`Could not find material area for slot ${materialData.slot}!`)
            return;
        }
        const options: IJungMaterialOptions = {
            material: materialAsset,
            area: area,
            mode: 'set',
            // Resolved (override applied, __missing__ stripped) name — this is what the AR service
            // receives via BlenderTransformer, so AR uses the same final material as the viewer.
            definition: {name: this.resolveLibraryName(materialData.assetName)},
            color: materialData.color
        }
        if (!materialData.primary && !materialData.conditional) {
            await this.handleStaticTextures(geoIdentifier, materialData.assetName, options)
            await this.addTextureColor(geoIdentifier, options, materialData.assetName)
            return options;
        }

        // Don't add ao to other materials!
        await this.addAoTexture(geoIdentifier, options, materialData)
        await this.addTexture(geoIdentifier, options, materialData.assetName)
        await this.addTextureColor(geoIdentifier, options, materialData.assetName)
        await this.handleStaticTextures(geoIdentifier, materialData.assetName, options)

        return options;
    }

    private findMaterialAreaName(geo: THREE.Object3D, slot: number): string|undefined {
        let counter: number = 0;
        let name: string | undefined
        geo.traverse((child) => {
            if (child instanceof THREE.Mesh) {
                // @ts-ignore
                let material: THREE.MeshPhysicalMaterial = child.material;
                if (counter === slot) {
                    name = material.name;
                }
                counter++;
            }
        })
        if (!name) {
            console.error(`Could not find material area in slot ${slot} in geo ${geo.name}`);
        }

        return name
    }

    private async addAoTexture(identifier: GeoIdentifier, options: IJungMaterialOptions, assignment: MaterialAssignment) {

        if (!assignment.primary && assignment.conditional) {
            const materialOptions = this.getOptions(identifier, '');
            if (!materialOptions || !materialOptions.addAoToConditional) {
                return;
            }
            console.log('Adding ao to conditional material area!');
        }
        const path = 'assets/materials/ao-textures/' + identifier.geoId + '.png'
        console.log(`Load ao: ${path}`);
        const ao = await this.textureLoader.getTextureFile(path);
        if (ao) {
            ao.encoding = THREE.LinearEncoding
            ao.flipY = false;
            options.aoMap = ao;
            options.aoPath = path;
            console.log(`Loaded ao: ${path}`);
        }
    }

    private async addTextureColor(identifier: GeoIdentifier,  options: IJungMaterialOptions, materialId: string) {
        const materialOption = this.getOptions(identifier, materialId);
        if (materialOption && materialOption.color) {
            console.log(`Add texture color for ${identifier.geoId}/${identifier.productId}`)
            options.textureColor = materialOption.color;
        }
    }

    private async addTexture(identifier: GeoIdentifier, options: IJungMaterialOptions, materialId: string) {
        await this.addDefaultTexture(identifier, options);
        const materialOption = this.getOptions(identifier, materialId)
        if (materialOption && materialOption.emissiveMap) {
            const emissivePath = `assets/materials/${materialOption.emissiveMap.map}`
            const emissiveTexture = await this.textureLoader.getTextureFile(emissivePath);
            if (emissiveTexture) {
                console.log(`Add emissive texture for ${identifier.geoId}/${identifier.productId}`)
                emissiveTexture.encoding = THREE.sRGBEncoding
                emissiveTexture.flipY = false;
                options.emissive = {
                    ...materialOption.emissiveMap,
                    texture: emissiveTexture,
                };
                options.emissivePath = emissivePath;
            }
        }
    }

    private async addDefaultTexture(identifier: GeoIdentifier, options: IJungMaterialOptions) {
        const texturePath = `assets/materials/color-textures/${identifier.productId}.jpg`;
        const texture = await this.textureLoader.getTextureFile(texturePath);
        const materialOption = this.getOptions(identifier, options.definition.name)
        if (texture) {
            console.log(`Add default texture for ${identifier.geoId}/${identifier.productId}`, materialOption)
            texture.encoding = THREE.sRGBEncoding
            texture.flipY = false;
            options.texture = texture;
            options.texturePath = texturePath;
            if (materialOption && materialOption.textureRepeat) {
                const vector = new THREE.Vector2(materialOption.textureRepeat.x, materialOption.textureRepeat.y);
                texture.repeat = vector;
                options.textureRepeat = vector;
            }
        }
    }

    private async handleStaticTextures(identifier: GeoIdentifier, materialId: string, options: IJungMaterialOptions) {
        const materialOption = this.getOptions(identifier, materialId);
        if (!materialOption || !materialOption.staticTextures) {
            console.log(`No static textures defined for ${identifier.productId} with ${materialId}`);
            return;
        }
        const areaTexture = materialOption.staticTextures.find((texture) => {
            return texture.area === options.area;
        });
        if (!areaTexture) {
            console.log(`No static textures defined for ${identifier.productId} with ${materialId} in ${options.area}`);
            return
        }
        console.log(`Static texture definition found for ${identifier.productId} with ${materialId} in ${options.area}`);
        const path = `assets/materials/${areaTexture.map}`
        const texture = await this.textureLoader.getTextureFile(path);
        if (texture) {
            console.log(`Add static texture ${path}`)
            texture.flipY = false;
            texture.encoding = THREE.sRGBEncoding
            options.texture = texture;
            options.texturePath = path;
            if (materialOption && materialOption.textureRepeat) {
                const vector = new THREE.Vector2(materialOption.textureRepeat.x, materialOption.textureRepeat.y);
                texture.repeat = vector;
                options.textureRepeat = vector;
            }
        }
    }

    private getOptions(identifier: GeoIdentifier, materialId: string): MaterialOption|undefined {
        let option;
        let materialSpecific = this.optionHandler.getOptionForProductWithMaterial(identifier.geoId, materialId)
        let productSpecific = this.optionHandler.getOptionForProductWithMaterial(identifier.productId, materialId);
        let assetSpecific = this.optionHandler.getOptionForProductWithMaterial(identifier.assetName, materialId)
        if (productSpecific) {
            option = productSpecific;
        } else if (assetSpecific) {
            option = assetSpecific;
        }else if (materialSpecific) {
            option = materialSpecific;
        }
        if (option) {
            return option;
        }

        option = this.optionHandler.getOptionForProduct(identifier.productId);
        if (!option) {
            option = this.optionHandler.getOptionForProduct(identifier.geoId);
        }
        if (!option) {
            option = this.optionHandler.getOptionForProduct(identifier.assetName)
        }
        if (option && !option.materialId) {
            return option
        }

        return undefined;
    }
}